// Admin panel — password-reset requests list, approve (issues 6-digit code) and reject.
import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";
import { KeyRound, CheckCircle2, XCircle, Loader2, Copy, Clock, Phone as PhoneIcon, User as UserIcon, X, ShieldAlert, Trash2, AlertTriangle, Wrench, RefreshCw } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

const STATUS_LABEL = {
  pending: { label: "قيد الانتظار", color: "bg-yellow-50 text-yellow-700" },
  approved: { label: "معتمد — بانتظار المستخدم", color: "bg-blue-50 text-blue-700" },
  used: { label: "تم الاستخدام", color: "bg-green-50 text-green-700" },
  rejected: { label: "مرفوض", color: "bg-red-50 text-red-700" },
  expired: { label: "منتهي الصلاحية", color: "bg-gray-100 text-gray-500" },
};

export default function PasswordResetRequestsModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [approvedCode, setApprovedCode] = useState(null); // {code, user, expires_at}
  const [filter, setFilter] = useState("pending");        // pending | all | attempts
  const [err, setErr] = useState("");
  const [attempts, setAttempts] = useState({ attempts: [], unmatched_last_24h: 0 });
  const [normalizeMsg, setNormalizeMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      if (filter === "attempts") {
        const { data } = await api.get("/admin/password-reset-attempts", { params: { limit: 50 } });
        setAttempts(data);
      } else {
        const params = filter === "pending" ? { status: "pending" } : {};
        const { data } = await api.get("/admin/password-reset-requests", { params });
        setItems(data.requests || []);
        setPendingCount(data.pending_count || 0);
      }
      // Always refresh unmatched count for the tab badge
      const at = await api.get("/admin/password-reset-attempts", { params: { limit: 1 } });
      setAttempts((a) => ({ ...a, unmatched_last_24h: at.data?.unmatched_last_24h || 0 }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل تحميل الطلبات");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    setBusyId(req.id); setErr("");
    try {
      const { data } = await api.post(`/admin/password-reset-requests/${req.id}/approve`);
      setApprovedCode(data);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "فشل الاعتماد"); }
    finally { setBusyId(null); }
  };

  const reject = async (req) => {
    if (!window.confirm(`رفض طلب استعادة كلمة المرور لـ ${req.name || req.phone}؟`)) return;
    setBusyId(req.id); setErr("");
    try {
      await api.post(`/admin/password-reset-requests/${req.id}/reject`);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "فشل الرفض"); }
    finally { setBusyId(null); }
  };

  const remove = async (req) => {
    if (!window.confirm("حذف هذا الطلب نهائياً؟")) return;
    setBusyId(req.id); setErr("");
    try {
      await api.delete(`/admin/password-reset-requests/${req.id}`);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "فشل الحذف"); }
    finally { setBusyId(null); }
  };

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="pwreset-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col talabya-shadow-float overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between border-b border-[var(--border)] bg-gradient-to-l from-primary-brand to-blue-500 text-white">
          <div className="flex items-center gap-2">
            <KeyRound size={18}/>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>طلبات استعادة كلمة المرور</div>
              <div className="text-[11px] text-white/85">قيد الانتظار: {pendingCount}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="pwreset-close"><X size={16}/></button>
        </div>

        <div className="px-5 py-3 flex items-center gap-2 border-b border-[var(--border)] bg-[var(--surface-alt)] flex-wrap">
          {[{k:"pending", label:"قيد الانتظار"}, {k:"all", label:"الكل"}, {k:"attempts", label:"محاولات لم تتطابق"}].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`relative px-3 py-1.5 rounded-lg text-xs font-black ${filter === t.k ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)] border border-[var(--border)]"}`}
              data-testid={`pwreset-filter-${t.k}`}
            >
              {t.label}
              {t.k === "attempts" && attempts.unmatched_last_24h > 0 && (
                <span className="absolute -top-1 -left-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center ring-1 ring-white">
                  {attempts.unmatched_last_24h}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={async () => {
              if (!window.confirm("سيتم توحيد صيغة كل أرقام الهواتف في قاعدة البيانات إلى +218... — هل أنت متأكد؟")) return;
              setNormalizeMsg("جاري التوحيد...");
              try {
                const { data } = await api.post("/admin/users/normalize-phones");
                setNormalizeMsg(`تم توحيد ${data.fixed} رقماً${data.conflicts ? ` — ${data.conflicts} تضارب` : ""}.`);
              } catch (e) { setNormalizeMsg("فشل التوحيد"); }
            }}
            className="mr-auto flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-black bg-blue-50 text-primary-brand hover:bg-blue-100 border border-primary-brand/30"
            data-testid="pwreset-normalize"
          >
            <Wrench size={12}/> توحيد صيغة الأرقام
          </button>
        </div>
        {normalizeMsg && (
          <div className="px-5 py-2 bg-blue-50 text-xs text-primary-brand border-b border-[var(--border)]" data-testid="pwreset-normalize-msg">{normalizeMsg}</div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)] flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin"/> جاري التحميل...
            </div>
          ) : filter === "attempts" ? (
            attempts.attempts.length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--text-secondary)]">
                <ShieldAlert size={28} className="mx-auto mb-2 opacity-40"/>
                لا توجد محاولات مسجّلة بعد.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="pwreset-attempts-list">
                {attempts.attempts.map((a) => (
                  <li key={a.id} className={`p-3 rounded-2xl border ${a.matched ? "border-green-200 bg-green-50/40" : "border-red-200 bg-red-50/40"}`} data-testid={`pwreset-attempt-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${a.matched ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>
                        {a.matched ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm truncate" dir="ltr">{a.identifier}</div>
                        <div className="text-[11px] text-[var(--text-secondary)] flex items-center gap-2 flex-wrap">
                          {a.matched ? (
                            <><span>مطابق:</span><b className="text-green-700">{a.user_name || "—"}</b></>
                          ) : (
                            <span className="text-red-600">لم يُطابق أي حساب</span>
                          )}
                          <span className="text-[var(--text-secondary)]">• {a.identifier_type === "email" ? "بريد" : "هاتف"}</span>
                          <span className="text-[var(--text-secondary)]"><Clock size={9} className="inline"/> {fmtDate(a.at)}</span>
                        </div>
                        {!a.matched && a.normalized !== a.identifier && (
                          <div className="text-[10px] mt-1 text-[var(--text-secondary)]" dir="ltr">
                            بحثنا عن: <span className="font-mono">{a.normalized}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--text-secondary)]">
              <ShieldAlert size={28} className="mx-auto mb-2 opacity-40"/>
              لا توجد طلبات {filter === "pending" ? "قيد الانتظار" : "بعد"}.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="pwreset-list">
              {items.map((r) => {
                const meta = STATUS_LABEL[r.status] || { label: r.status, color: "bg-gray-50 text-gray-600" };
                return (
                  <li key={r.id} className="bg-white border border-[var(--border)] rounded-2xl p-3" data-testid={`pwreset-item-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-primary-brand flex items-center justify-center shrink-0">
                        <UserIcon size={16}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm truncate">{r.name || "(بدون اسم)"}</div>
                        <div className="text-[11px] text-[var(--text-secondary)] flex items-center gap-1" dir="ltr">
                          <PhoneIcon size={11}/> {r.phone}
                        </div>
                        <div className="text-[10px] text-[var(--text-secondary)] flex items-center gap-1">
                          <Clock size={10}/> {fmtDate(r.last_requested_at || r.requested_at)}
                          {(r.request_count || 1) > 1 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 font-bold text-[9px]">
                              كرّر {r.request_count} مرات
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${meta.color}`}>{meta.label}</span>
                    </div>
                    {r.status === "pending" || r.status === "approved" ? (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          onClick={() => approve(r)}
                          disabled={busyId === r.id}
                          className="flex-1 min-w-[120px] py-2 rounded-xl bg-primary-brand text-white font-black text-xs flex items-center justify-center gap-1 disabled:opacity-60"
                          data-testid={`pwreset-approve-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
                          {r.status === "approved" ? "إعادة إصدار الكود" : "اعتماد وإصدار كود"}
                        </button>
                        <button
                          onClick={() => reject(r)}
                          disabled={busyId === r.id}
                          className="py-2 px-3 rounded-xl bg-red-50 text-red-600 font-black text-xs flex items-center gap-1"
                          data-testid={`pwreset-reject-${r.id}`}
                        >
                          <XCircle size={12}/> رفض
                        </button>
                      </div>
                    ) : (
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => remove(r)}
                          className="text-[11px] text-red-500 flex items-center gap-1 hover:underline"
                          data-testid={`pwreset-delete-${r.id}`}
                        >
                          <Trash2 size={11}/> حذف
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {err && <div className="mt-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="pwreset-err">{err}</div>}
        </div>
      </div>

      {/* Approved code modal — shown after approve */}
      {approvedCode && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setApprovedCode(null)}/>
          <div className="relative bg-white rounded-3xl w-full max-w-md p-6 text-center talabya-shadow-float">
            <div className="w-14 h-14 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={26}/>
            </div>
            <h3 className="font-black text-xl mb-1" style={{fontFamily:"Cairo"}}>الكود جاهز — شاركه مع المستخدم</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-3">
              {approvedCode.user?.name} — <span dir="ltr">{approvedCode.user?.phone}</span>
            </p>
            <div className="p-5 rounded-2xl bg-blue-50 border-2 border-primary-brand mb-3">
              <div className="text-[11px] text-primary-brand mb-1">الكود المكوّن من 6 أرقام</div>
              <div className="font-mono font-black text-4xl tracking-[8px] text-primary-brand" data-testid="pwreset-code-value">{approvedCode.code}</div>
            </div>
            <button
              onClick={() => copy(approvedCode.code)}
              className="w-full py-3 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center gap-2 mb-2"
              data-testid="pwreset-copy-code"
            >
              <Copy size={14}/> نسخ الكود
            </button>
            <p className="text-[10px] text-[var(--text-secondary)] mb-3">
              صالح 30 يوماً. يمكن استخدامه مرة واحدة فقط. بعد 5 محاولات خاطئة يجب إصدار كود جديد.
            </p>
            <button onClick={() => setApprovedCode(null)} className="text-sm text-[var(--text-secondary)] font-bold">إغلاق</button>
          </div>
        </div>
      )}
    </div>
  );
}
